import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverFunil from "@/assets/cover-funil-vendas.jpg";
import postFunilDashboard from "@/assets/mentoria-3.jpg";
import postFunilEquipe from "@/assets/mentoria-6.jpg";
import postCoverNegociacao from "@/assets/cover-negociacao-b2b.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  BarChart3,
  Star,
  AlertTriangle,
  Lightbulb,
  Calendar,
  TrendingUp,
  Filter,
  GitBranch,
  LineChart,
  Database,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const FunilVendas2026 = () => {
  const navigate = useNavigate();

  const checklist = [
    {
      number: "01",
      title: "Pare de Confundir Funil Com CRM — Eles Não São a Mesma Coisa",
      icon: Filter,
      problem: "Sua equipe enche o CRM de lead mas ninguém sabe responder 'quantos vão fechar este mês'?",
      action:
        "Em 2026, ter Pipedrive, HubSpot ou Salesforce instalado não significa ter funil de vendas. CRM é a ferramenta. Funil é o método: etapas claras, critérios objetivos de avanço, gatilhos de saída, SLA por etapa e taxa de conversão medida. A maioria dos times comerciais mistura os dois e termina com pipeline inflado, previsão errada e gestor cobrando atividade em vez de resultado. O funil verdadeiro responde três perguntas em 30 segundos: onde está cada lead, por que ele está parado e quanto vai fechar nos próximos 30, 60 e 90 dias.",
      checkItems: [
        "Mapeie as etapas reais do seu funil (de prospecção a pós-venda) em até 7 estágios",
        "Defina critério objetivo de avanço para cada etapa — não 'achismo' do vendedor",
        "Estabeleça SLA máximo de permanência em cada estágio (ex: 14 dias em proposta)",
        "Crie regra de saída: leads parados além do SLA voltam para nutrição ou são perdidos",
        "Treine o time a usar CRM como reflexo do funil, não como agenda pessoal",
      ],
      story: `Marivaldo Souza, CEO da Triplos Tecnologia, conta que o ponto de virada do time aconteceu quando pararam de "encher o CRM" e passaram a respeitar critérios de avanço por etapa. "Em 60 dias, o pipeline caiu 40% em volume e a taxa de conversão subiu 28%. O que parecia oportunidade era ruído."`,
    },
    {
      number: "02",
      title: "Defina o ICP Antes de Encher o Topo do Funil",
      icon: Target,
      problem: "Você prospecta qualquer empresa com pulso e depois reclama que ninguém fecha?",
      action:
        "Em 2026, com cold outbound mais caro e mais bloqueado por filtros de IA, prospectar fora do ICP (Ideal Customer Profile) é queimar dinheiro e moral do time. Antes de gerar 1 lead, defina por escrito: porte da empresa, segmento, cargo do decisor, dor crítica, gatilho de compra, ticket médio possível e ciclo de vendas estimado. ICP claro filtra o topo do funil e multiplica a conversão de meio e fundo. Time que prospecta tudo, fecha pouco. Time que prospecta o ICP certo, fecha muito — com menos esforço.",
      checkItems: [
        "Documente o ICP em uma página com 8 critérios objetivos",
        "Crie um anti-ICP: quem você não quer no funil mesmo que peça proposta",
        "Audite o pipeline atual e marque cada oportunidade como dentro ou fora do ICP",
        "Bloqueie tempo do time apenas para prospecção dentro do ICP nos primeiros 90 dias",
      ],
      story: null,
    },
    {
      number: "03",
      title: "Construa Etapas Curtas, Mensuráveis e Sem Sobreposição",
      icon: GitBranch,
      problem: "Vendedores discutem se o lead está em 'qualificação' ou 'proposta' porque os estágios se confundem?",
      action:
        "Etapa de funil sem critério objetivo é fonte de pipeline mentiroso. Em 2026, padrão de mercado para vendas B2B consultivas é: 1) Lead, 2) Qualificação (MQL/SQL), 3) Descoberta, 4) Diagnóstico/Demo, 5) Proposta, 6) Negociação, 7) Fechamento. Cada etapa precisa de definição binária: ou o lead atendeu o critério para avançar, ou não atendeu. Sem 'meio termo'. Quando o time discute em qual etapa está o lead, o funil já está quebrado.",
      checkItems: [
        "Limite o funil a no máximo 7 etapas — mais que isso vira burocracia",
        "Escreva o critério de avanço de cada etapa em uma frase verificável",
        "Garanta que cada etapa tenha um entregável claro (ata de descoberta, proposta enviada, etc.)",
        "Revise mensalmente: alguma etapa não está sendo usada? Funda ou elimine",
      ],
      story: null,
    },
    {
      number: "04",
      title: "Meça Taxa de Conversão Entre Cada Etapa — Não Só do Topo ao Fundo",
      icon: BarChart3,
      problem: "Você só olha 'leads gerados x fechados' e não sabe onde o funil afunila?",
      action:
        "Métrica agregada esconde gargalo. Em 2026, time comercial profissional mede taxa de conversão de cada etapa para a próxima: lead→MQL, MQL→SQL, SQL→proposta, proposta→fechamento. Quando uma etapa converte muito menos que o benchmark, ali está o problema. Se MQL→SQL converte 12%, mas proposta→fechamento converte só 18%, o gargalo é negociação e fechamento — não prospecção. Sem essa visão, o time gasta energia onde não dói.",
      checkItems: [
        "Crie dashboard com taxa de conversão entre cada par de etapas",
        "Defina benchmark interno para cada conversão e meta de melhoria trimestral",
        "Em reunião semanal de pipeline, comece sempre pela etapa de pior conversão",
        "Atribua plano de ação específico para a etapa de maior queda — não dilua esforço",
      ],
      story: null,
    },
    {
      number: "05",
      title: "Implemente Forecast de Vendas Por Probabilidade, Não Por Otimismo",
      icon: LineChart,
      problem: "Sua previsão de fechamento sempre erra para mais e o board já não confia mais em você?",
      action:
        "Forecast por 'sentimento do vendedor' está morto. Em 2026, previsão séria usa probabilidade ponderada por etapa do funil: descoberta 10%, diagnóstico 25%, proposta 50%, negociação 70%, fechamento 90%. Multiplique valor do deal pela probabilidade da etapa e some — esse é seu forecast estatístico. Vendedor pode (e deve) ajustar para cima ou para baixo com justificativa por escrito. Sem método, forecast é torcida — e empresa que opera por torcida não escala.",
      checkItems: [
        "Defina probabilidade-padrão por etapa do funil e documente",
        "Calcule forecast ponderado semanalmente no fechamento da sexta-feira",
        "Exija justificativa escrita para qualquer ajuste manual de probabilidade",
        "Acompanhe acurácia de forecast por vendedor — quem erra muito precisa de mentoria",
        "Compare forecast x realizado mensal para calibrar percentuais",
      ],
      story: `Silvete Araujo, Diretora de Negócios na Zentai Benefícios, implementou forecast ponderado em 2025. Em 4 meses, a diferença entre previsão e realizado caiu de 38% para 9%. "O board voltou a confiar nas reuniões de pipeline — e isso destravou orçamento para contratar mais vendedores."`,
    },
    {
      number: "06",
      title: "Higienize o Pipeline Todo Mês — Lead Velho Mente",
      icon: Database,
      problem: "Você tem deals parados há 90, 120, 180 dias dizendo que 'vai fechar mês que vem'?",
      action:
        "Pipeline empolado por lead velho é o veneno silencioso da operação comercial. Lead que não avança em 2x o ciclo médio de vendas tem 90% de chance de nunca fechar. Em 2026, com IA capaz de identificar inatividade real (sem reply, sem reunião, sem proposta atualizada), higienizar é obrigatório. Crie regra automática: deal parado além do SLA volta para nutrição ou é marcado como perdido. Forecast confiável só existe quando o pipeline reflete realidade — não esperança.",
      checkItems: [
        "Defina SLA de tempo máximo por etapa e auditoria mensal obrigatória",
        "Crie 1 dia por mês de 'pipeline cleanup' bloqueado na agenda do time",
        "Marque como perdido todo deal sem atividade real em 2x o ciclo médio",
        "Categorize motivo de perda — sem isso, não dá para corrigir o funil",
        "Celebre publicamente quem limpa pipeline — gera cultura saudável",
      ],
      story: null,
    },
    {
      number: "07",
      title: "Use IA Para Priorizar Onde Investir Tempo do Time",
      icon: Zap,
      problem: "Vendedor trabalha por ordem de chegada do lead, não por probabilidade real de fechar?",
      action:
        "Em 2026, lead scoring com IA deixou de ser luxo. Modelos atuais analisam comportamento (abriu e-mail, baixou material, agendou reunião), perfil firmográfico (porte, segmento, tecnologia) e histórico de interação para ranquear cada lead por probabilidade de fechar nos próximos 30 dias. Vendedor que ataca o top 20% do scoring fecha 3x mais que quem trabalha por ordem de fila. Plataformas como HubSpot, Pipedrive, Lemlist e o próprio Vendas Trainer já trazem essa camada nativa.",
      checkItems: [
        "Ative lead scoring em CRM (ou contrate camada de IA externa)",
        "Bloqueie 70% do tempo do time para os 20% melhores leads por scoring",
        "Revise os critérios de scoring trimestralmente com base em conversão real",
        "Treine o time a ler scoring antes da primeira ligação — economiza energia",
      ],
      story: null,
    },
    {
      number: "08",
      title: "Faça Reunião Semanal de Pipeline Que Decide, Não Que Atualiza",
      icon: Calendar,
      problem: "Sua reunião de pipeline vira teatro de status report onde ninguém decide nada?",
      action:
        "Reunião de pipeline mal conduzida queima 4 horas/semana de cada vendedor sem gerar progresso. Em 2026, reunião profissional tem agenda fixa: 1) Top 5 deals da semana (próximo passo e bloqueio), 2) Deals parados além do SLA (avançar ou perder), 3) Forecast do mês (compromisso individual por vendedor), 4) Ações concretas com responsável e data. Sem 'comentário geral'. Quem chega sem preparo não participa. Reunião de pipeline existe para destravar negócio — não para narrar atividade.",
      checkItems: [
        "Padronize agenda escrita enviada 24h antes da reunião",
        "Limite a duração a 45 minutos para time de até 8 vendedores",
        "Termine com ata: ação, responsável, prazo — enviada por e-mail em 1h",
        "Exija forecast individual semanal por escrito antes da reunião",
        "Convide leads de pré-vendas e marketing 1x por mês para alinhar topo do funil",
      ],
      story: null,
    },
    {
      number: "09",
      title: "Integre Marketing, Pré-Vendas e Vendas Em Um Único Funil",
      icon: Users,
      problem: "Marketing entrega lead, pré-vendas reclama da qualidade e vendas culpa os dois?",
      action:
        "Em 2026, funil fragmentado é sentença de morte para previsibilidade. Marketing, pré-vendas e vendas precisam compartilhar definição de MQL, SQL, critérios de aceite e SLA de handoff. SLA típico: pré-vendas tem 24h para qualificar lead de marketing; vendas tem 48h para iniciar discovery de SQL aceito. Sem isso, lead esfria, time aponta dedo e ninguém é dono do resultado. Funil de receita previsível só existe quando os três times olham o mesmo dashboard, com a mesma definição.",
      checkItems: [
        "Documente em uma página: definição de MQL, SQL, SAL e critério de aceite",
        "Implemente SLA escrito de handoff entre cada equipe",
        "Mostre o mesmo dashboard nas reuniões de marketing, pré-vendas e vendas",
        "Faça reunião quinzenal de RevOps com líderes dos três times",
        "Atribua bônus cruzado: marketing ganha por SQL aceito, não por MQL gerado",
      ],
      story: null,
    },
    {
      number: "10",
      title: "Treine o Time Em Simulação Para Cada Etapa Do Funil",
      icon: TrendingUp,
      problem: "Seu time aprende a operar funil errando com cliente real e queimando deals que estavam prontos?",
      action:
        "Cada etapa do funil exige habilidade diferente: prospecção exige resiliência e cadência; descoberta exige escuta e SPIN; proposta exige clareza e ROI; negociação exige BATNA e ancoragem. Treinar tudo junto, no campo, é caro e demorado. Em 2026, simulação com IA permite isolar a habilidade fraca de cada vendedor e treinar especificamente até dominar. Quem treina semanalmente em simulação avança no funil com 40% mais velocidade e taxa de conversão estágio-a-estágio 25% maior. O Vendas Trainer foi construído para esse tipo de treino cirúrgico, etapa por etapa.",
      checkItems: [
        "Mapeie a habilidade dominante exigida em cada etapa do funil",
        "Diagnostique o gap de cada vendedor em cada habilidade",
        "Implemente role-play semanal específico para a etapa de pior conversão",
        "Use simulação de IA para repetição entre as sessões com gestor",
        "Meça evolução individual com score objetivo a cada simulação",
      ],
      story: `Times que treinam semanalmente em simulação por etapa do funil reduzem o ciclo médio de vendas em 22% e aumentam a taxa de fechamento em 31% nos primeiros 90 dias. Não é teoria — é resultado consistente em operações que adotaram o método.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Funil de Vendas em 2026: Como Construir Pipeline Previsível Que Triplica Conversões",
    description:
      "Guia completo de funil de vendas em 2026: ICP, etapas, conversão, forecast ponderado, lead scoring com IA, higienização e treino por etapa.",
    author: {
      "@type": "Person",
      name: "Junior Bianchim",
      jobTitle: "Especialista em Treinamento de Equipes Comerciais",
    },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-06-01",
    dateModified: "2026-06-01",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/funil-vendas-2026",
    },
    image: "https://bianchimbusiness.com.br/og-funil-vendas-2026.jpg",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Qual a diferença entre funil de vendas e CRM?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "CRM é a ferramenta que armazena dados de clientes e oportunidades. Funil de vendas é o método que define etapas, critérios objetivos de avanço, SLA por estágio e taxa de conversão medida. Ter CRM sem funil estruturado é ter pipeline inflado e previsão errada.",
        },
      },
      {
        "@type": "Question",
        name: "Quantas etapas deve ter um funil de vendas B2B em 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "O padrão de mercado para vendas B2B consultivas é entre 5 e 7 etapas: Lead, Qualificação (MQL/SQL), Descoberta, Diagnóstico/Demo, Proposta, Negociação e Fechamento. Mais que 7 vira burocracia; menos que 5 perde granularidade de gestão.",
        },
      },
      {
        "@type": "Question",
        name: "Como fazer forecast de vendas que não erra?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Use probabilidade ponderada por etapa do funil (descoberta 10%, diagnóstico 25%, proposta 50%, negociação 70%, fechamento 90%), multiplique o valor do deal pela probabilidade e some. Ajustes manuais só com justificativa escrita. Compare forecast x realizado mensalmente para calibrar.",
        },
      },
      {
        "@type": "Question",
        name: "O que é lead scoring com IA?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Lead scoring com IA analisa comportamento, perfil firmográfico e histórico de interação para ranquear leads por probabilidade de fechar nos próximos 30 dias. Vendedor que ataca os 20% melhores do scoring fecha em média 3x mais do que quem trabalha por ordem de chegada.",
        },
      },
      {
        "@type": "Question",
        name: "Com que frequência higienizar o pipeline?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Mensalmente, sem exceção. Lead parado além de 2x o ciclo médio de vendas tem 90% de chance de não fechar. Marque como perdido, categorize o motivo e libere foco do time para oportunidades reais. Pipeline empolado por lead velho mata o forecast.",
        },
      },
    ],
  };

  const relatedPosts = [
    {
      title: "Negociação B2B em 2026: Como Conduzir Reuniões Que Fecham Grandes Contratos",
      readTime: "23 min",
      link: "/posts/negociacao-b2b-2026",
      image: postCoverNegociacao,
    },
    {
      title: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível",
      readTime: "22 min",
      link: "/posts/pre-vendas-sdr-bdr-2026",
      image: postCoverPreVendas,
    },
    {
      title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
      readTime: "25 min",
      link: "/posts/13-estrategias-vendas-2026",
      image: postCoverEstrategias,
    },
  ];

  return (
    <>
      <SEOHead
        title="Funil de Vendas em 2026: Pipeline Previsível que Triplica Conversões"
        description="Guia completo de funil de vendas em 2026: ICP, etapas, conversão por estágio, forecast ponderado, lead scoring com IA, higienização e treino por etapa. 10 passos práticos."
        keywords="funil de vendas, pipeline de vendas, forecast de vendas, lead scoring, crm, conversão de vendas, gestão comercial, ICP, MQL SQL, funil 2026"
        canonicalUrl="https://bianchimbusiness.com.br/posts/funil-vendas-2026"
        ogType="article"
        articlePublishedTime="2026-06-01"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverFunil}
                  alt="Junior Bianchim em mentoria de estruturação de funil de vendas e pipeline previsível"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Gestão Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Funil de Vendas em 2026:{" "}
                    <span className="text-gradient-brand">
                      Como Construir Pipeline Previsível Que Triplica Conversões
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>18 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>01 Jun 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">Quantos negócios da sua previsão deste mês vão realmente fechar?</strong> Se você hesitou para responder, seu funil de vendas está mentindo para você. Em 2026, com ciclo de venda B2B mais longo, comprador mais técnico e CFO cobrando previsibilidade trimestral, operar sem funil estruturado é dirigir no escuro a 120 km/h.
                    </p>
                    <p>
                      A boa notícia: funil de vendas não é arte — é engenharia. Etapas claras, critérios objetivos, taxa de conversão medida, forecast ponderado e IA priorizando leads. <strong className="text-foreground">Pipeline previsível é resultado de método aplicado, não de talento individual</strong>. E método se constrói em sequência, peça por peça.
                    </p>
                    <p>
                      Este guia é o playbook que aplico em times de empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros, Zentai Benefícios e Triplos Tecnologia. <strong className="text-foreground">10 passos para estruturar funil de vendas profissional em 2026, eliminar pipeline empolado, acertar forecast e triplicar a taxa de conversão por etapa.</strong>
                    </p>
                    <Card className="p-6 border-primary/20 bg-primary/5 my-8">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-foreground mb-2">Antes de continuar, responda honestamente:</p>
                          <p className="text-muted-foreground">
                            Olhando seu pipeline agora, <strong className="text-foreground">quantos deals estão parados há mais de 2x o seu ciclo médio de vendas?</strong> Esse número é o tamanho da mentira que você está contando ao board todo mês.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Image - dashboard */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postFunilDashboard}
                      alt="Time comercial em sessão de treinamento e análise de pipeline"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* The 10 Steps */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    Os 10 Passos Para Construir um Funil de Vendas Previsível em 2026
                  </h2>
                  <p className="text-muted-foreground mb-10">
                    Cada passo é diagnóstico + ação + checklist. Implemente em sequência ou ataque primeiro o que mais dói no seu pipeline atual.
                  </p>

                  <div className="space-y-16">
                    {checklist.map((item, index) => {
                      const Icon = item.icon;

                      // Passo 3 — CTA grátis
                      if (index === 2) {
                        return (
                          <div key={index} id={`passo-${item.number}`}>
                            <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/10 via-card to-accent/10 border-primary/40 text-center">
                              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-7 h-7 text-primary" />
                              </div>
                              <p className="text-sm font-semibold text-primary mb-2">Pausa estratégica · Passo {item.number}</p>
                              <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                                Antes de continuar: <span className="text-gradient-brand">teste o Vendas Trainer grátis</span>
                              </h3>
                              <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                                Você acabou de ver os 2 primeiros passos para construir um funil previsível. O próximo passo natural é treinar o time em cada etapa — e o Vendas Trainer faz isso com simulações de IA, feedback instantâneo e métricas por estágio do funil. <strong className="text-foreground">Crie sua conta grátis em menos de 1 minuto.</strong>
                              </p>
                              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button
                                  size="lg"
                                  onClick={() => navigate("/auth")}
                                  className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
                                >
                                  Testar Grátis Agora
                                  <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                                <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                                  Conhecer a plataforma
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-4">Sem cartão de crédito · Acesso imediato</p>
                            </Card>
                          </div>
                        );
                      }

                      const SectionHead = (
                        <div className="flex items-center gap-4 mb-6">
                          <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-extrabold text-xl">{item.number}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Passo {item.number}</p>
                            <h3 className="text-xl sm:text-2xl font-bold text-foreground">{item.title}</h3>
                          </div>
                          <Icon className="w-6 h-6 text-primary flex-shrink-0 hidden sm:block" />
                        </div>
                      );

                      let body: JSX.Element;

                      switch (index) {
                        case 0:
                          // 3 COLUNAS — três cartões temáticos
                          body = (
                            <div className="grid md:grid-cols-3 gap-4">
                              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
                                <AlertTriangle className="w-5 h-5 text-destructive mb-3" />
                                <p className="text-xs font-bold uppercase tracking-wider text-destructive mb-2">O sintoma</p>
                                <p className="text-sm text-muted-foreground">{item.problem}</p>
                              </div>
                              <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
                                <Lightbulb className="w-5 h-5 text-primary mb-3" />
                                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">A virada</p>
                                <p className="text-sm text-muted-foreground">{item.action}</p>
                              </div>
                              <div className="rounded-xl border border-border bg-muted/30 p-5">
                                <CheckCircle className="w-5 h-5 text-foreground mb-3" />
                                <p className="text-xs font-bold uppercase tracking-wider text-foreground mb-2">Aplique hoje</p>
                                <ul className="space-y-2 text-sm text-muted-foreground">
                                  {item.checkItems.slice(0, 4).map((c, i) => (
                                    <li key={i} className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />{c}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          );
                          break;

                        case 1:
                          // 4 COLUNAS — 4 pilares do ICP
                          body = (
                            <>
                              <p className="text-base text-muted-foreground mb-4 leading-relaxed">
                                <span className="text-destructive font-semibold">Sintoma: </span>{item.problem}
                              </p>
                              <p className="text-base text-foreground mb-6 leading-relaxed">{item.action}</p>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {item.checkItems.map((check, i) => (
                                  <div key={i} className="p-5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 to-transparent">
                                    <div className="text-3xl font-extrabold text-primary mb-2">0{i + 1}</div>
                                    <p className="text-sm text-muted-foreground">{check}</p>
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                          break;

                        case 3:
                          // 5 COLUNAS — timeline horizontal
                          body = (
                            <>
                              <div className="rounded-2xl bg-muted/30 border border-border p-6 mb-8">
                                <p className="text-base text-foreground leading-relaxed"><span className="text-destructive font-semibold">{item.problem} </span>{item.action}</p>
                              </div>
                              <div className="relative">
                                <div className="hidden lg:block absolute top-6 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-primary/40 via-accent/40 to-primary/40" />
                                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 relative">
                                  {item.checkItems.map((check, i) => (
                                    <div key={i} className="relative">
                                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground font-bold flex items-center justify-center mx-auto mb-3 relative z-10">
                                        {i + 1}
                                      </div>
                                      <p className="text-sm text-muted-foreground text-center">{check}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          );
                          break;

                        case 4:
                          // 2 COLUNAS — antes vs depois
                          body = (
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="rounded-xl border-2 border-destructive/30 bg-destructive/5 p-6">
                                <p className="text-xs font-bold uppercase tracking-wider text-destructive mb-3">Como está hoje</p>
                                <p className="text-base text-foreground leading-relaxed">{item.problem}</p>
                              </div>
                              <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-6">
                                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-3">Como vai ficar</p>
                                <p className="text-base text-foreground leading-relaxed mb-4">{item.action}</p>
                                <ul className="space-y-2">
                                  {item.checkItems.map((c, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-muted-foreground"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />{c}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          );
                          break;

                        case 5:
                          // 1/3 + 2/3
                          body = (
                            <div className="grid md:grid-cols-3 gap-6">
                              <div className="md:col-span-1 rounded-2xl bg-gradient-to-br from-destructive/10 to-transparent border border-destructive/20 p-6 flex flex-col justify-center">
                                <AlertTriangle className="w-8 h-8 text-destructive mb-4" />
                                <p className="text-lg font-bold text-foreground leading-snug">{item.problem}</p>
                              </div>
                              <div className="md:col-span-2 space-y-4">
                                <p className="text-base text-muted-foreground leading-relaxed">{item.action}</p>
                                <div className="grid sm:grid-cols-2 gap-3">
                                  {item.checkItems.map((check, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                      <span className="text-sm text-muted-foreground">{check}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                          break;

                        case 6:
                          // 2/3 + 1/3
                          body = (
                            <div className="grid md:grid-cols-3 gap-6">
                              <div className="md:col-span-2 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-accent/5 p-6">
                                <p className="text-base text-foreground leading-relaxed mb-5">{item.action}</p>
                                <div className="space-y-2">
                                  {item.checkItems.map((check, i) => (
                                    <div key={i} className="flex items-center gap-4 py-2 border-b border-border last:border-0">
                                      <span className="text-2xl font-extrabold text-primary/60 w-8 flex-shrink-0">{i + 1}</span>
                                      <span className="text-sm text-foreground">{check}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="md:col-span-1 rounded-2xl bg-destructive/5 border border-destructive/20 p-6 flex flex-col justify-center">
                                <AlertTriangle className="w-6 h-6 text-destructive mb-3" />
                                <p className="text-xs font-bold uppercase tracking-wider text-destructive mb-2">Sinal de alerta</p>
                                <p className="text-sm text-muted-foreground">{item.problem}</p>
                              </div>
                            </div>
                          );
                          break;

                        case 7:
                          // 1 COLUNA — manifesto
                          body = (
                            <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border border-primary/30 p-8 sm:p-10">
                              <p className="text-destructive font-semibold mb-4">{item.problem}</p>
                              <p className="text-lg text-foreground leading-relaxed mb-6">{item.action}</p>
                              <div className="border-t border-border pt-6">
                                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-4">Agenda da reunião que decide</p>
                                <div className="flex flex-wrap gap-2">
                                  {item.checkItems.map((check, i) => (
                                    <span key={i} className="px-3 py-2 rounded-full bg-background border border-primary/30 text-sm text-muted-foreground">
                                      <span className="text-primary font-bold mr-1">{i + 1}.</span>{check}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                          break;

                        case 8:
                          // 3 COLUNAS variação — Marketing / Pré-vendas / Vendas
                          body = (
                            <>
                              <p className="text-base text-foreground mb-6 leading-relaxed">
                                <span className="text-destructive font-semibold">{item.problem} </span>{item.action}
                              </p>
                              <div className="grid md:grid-cols-3 gap-4 mb-6">
                                {["Marketing", "Pré-vendas", "Vendas"].map((area, i) => (
                                  <div key={i} className="rounded-xl bg-gradient-to-b from-primary/10 to-transparent border border-primary/20 p-5 text-center">
                                    <Users className="w-8 h-8 text-primary mx-auto mb-3" />
                                    <p className="font-bold text-foreground">{area}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Mesmo dashboard · Mesmo SLA · Mesma definição</p>
                                  </div>
                                ))}
                              </div>
                              <div className="space-y-2">
                                {item.checkItems.map((check, i) => (
                                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-muted-foreground">{check}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                          break;

                        case 9:
                        default:
                          // Fechamento — destaque + grid 3
                          body = (
                            <>
                              <div className="rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/30 p-6 mb-6">
                                <Star className="w-8 h-8 text-primary mb-3" />
                                <p className="text-lg text-foreground leading-relaxed font-medium">{item.action}</p>
                              </div>
                              <p className="text-sm text-destructive font-semibold mb-4">{item.problem}</p>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {item.checkItems.map((check, i) => (
                                  <div key={i} className="p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors">
                                    <CheckCircle className="w-4 h-4 text-primary mb-2" />
                                    <p className="text-sm text-muted-foreground">{check}</p>
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                      }

                      return (
                        <div key={index} id={`passo-${item.number}`}>
                          {SectionHead}
                          {body}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Image - equipe */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postFunilEquipe}
                      alt="Mentoria presencial de funil de vendas com líderes comerciais em reunião estratégica"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Summary */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    Resumo: O Mapa Completo do Funil de Vendas Profissional em 2026
                  </h2>
                  <Card className="p-6 sm:p-8 border-primary/20">
                    <div className="space-y-4">
                      {checklist.map((item, index) => (
                        <a
                          key={index}
                          href={`#passo-${item.number}`}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{item.number}</span>
                          </div>
                          <span className="text-foreground font-medium group-hover:text-primary transition-colors flex-1">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </a>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Funil de Vendas Não é Planilha — É Sistema Operacional do Time Comercial
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      O time que opera por intuição sempre perde para quem opera por método. Em 2026, com IA reduzindo custo de prospecção, comprador mais técnico e ciclos mais longos, a única diferença sustentável entre operações que crescem e operações que estagnam é <strong className="text-foreground">previsibilidade de receita</strong>. E previsibilidade só nasce de funil estruturado, etapas mensuráveis e forecast ponderado por probabilidade.
                    </p>
                    <p>
                      ICP claro. Etapas curtas e binárias. Conversão medida entre cada par. Forecast por probabilidade. Pipeline higienizado. Lead scoring com IA. Reunião que decide. Marketing, pré-vendas e vendas no mesmo dashboard. Treino semanal em simulação por etapa. <strong className="text-foreground">Quando esses 10 elementos rodam juntos, o gestor para de torcer pelo fechamento — passa a prever.</strong>
                    </p>
                    <p>
                      A meta do trimestre que você está perseguindo vai cair com método ou vai morrer em pipeline empolado. A diferença está em fazer engenharia de funil antes da próxima reunião de board.
                    </p>
                    <p>
                      <strong className="text-foreground">Comece pelo passo 01 hoje. Implemente um passo por semana. Em 90 dias, sua conversão sobe, seu forecast acerta e seu time deixa de operar no caos — passa a operar com previsibilidade.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <Link key={index} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title} - conteúdo complementar sobre vendas e funil`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em treinamento de equipes comerciais e estruturação de funil de vendas"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Treine Seu Time Em Cada Etapa do Funil</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, sua equipe pratica prospecção, descoberta, proposta e negociação em simulações reais com IA.
                      Feedback instantâneo, gamificação e métricas claras por etapa do funil.
                      <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default FunilVendas2026;
